import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import Button from '@renderer/components/bahela/Button'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela-green.webp'
import pickFrameId from '/assets/bahela/pick-frame-id.webp'
import pickFrameEn from '/assets/bahela/pick-frame-en.webp'

const FramePage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)

  const { frames, getPageSettings } = useConfigStore()
  const { setSessionState, logActivity, userPreferences } = useSessionStateStore()

  // Auto-advance timeout is driven by the saved project config; 0 = no timeout.
  const frameTimeout = getPageSettings('frame')?.frameTimeout || 0
  const [remainingTime, setRemainingTime] = useState<number>(frameTimeout)

  const sortedFrames = useMemo(() => {
    const pinId = userPreferences.frameId
    // Custom frames are only shown when they are the voucher-linked frame for this session.
    // Without an applied custom-frame voucher (no pinId or pinId doesn't match), they stay hidden.
    const visibleFrames = frames.filter((f) => !f.isCustomFrame || f._id === pinId)
    if (!pinId || visibleFrames.length === 0) return visibleFrames
    const ix = visibleFrames.findIndex((f) => f._id === pinId)
    if (ix <= 0) return visibleFrames
    const next = [...visibleFrames]
    const [pinned] = next.splice(ix, 1)
    return [pinned, ...next]
  }, [frames, userPreferences.frameId])

  // Initialize countdown hook
  const frameCountdown = useCountdown(frameTimeout)

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  useEffect((): (() => void) => {
    if (sortedFrames.length > 0) {
      const pinId = userPreferences.frameId
      const pinOk = pinId && sortedFrames.some((f) => f._id === pinId)
      setSelectedFrame((prev) => {
        if (pinOk && pinId) return pinId
        if (prev && sortedFrames.some((f) => f._id === prev)) return prev
        return sortedFrames[0]._id
      })
    }

    if (frameTimeout > 0) {
      frameCountdown.setOnTick(() => {
        setRemainingTime(frameCountdown.getRemainingSeconds())
      })

      frameCountdown.setOnComplete(() => {
        handleContinue()
      })

      frameCountdown.start()
    }

    return () => {
      frameCountdown.stop()
    }
  }, [sortedFrames, userPreferences.frameId])

  const handleFrameClick = (frameId: string): void => {
    setSelectedFrame(frameId)
  }

  const handleContinue = async (): Promise<void> => {
    if (!selectedFrame) return

    const frame = sortedFrames.find((f) => f._id === selectedFrame)
    if (!frame) return

    // Find first photo type image and get its ratio
    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId: selectedFrame,
        framePhotoRatio: photoRatio
      }
    })

    // Log frame selection activity
    const frameName = frame.name
    logActivity(`Selected frame: ${frameName || selectedFrame}`, {
      frameId: selectedFrame
    })

    navigate(`/${projectId}/select`)
  }

  // Call all hooks at the top level
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedPickFrame = useCachedImage(i18n.language === 'id' ? pickFrameId : pickFrameEn)
  const cachedFrameThumbnail = useCachedImage(
    sortedFrames.find((f) => f._id === selectedFrame)?.thumbnail || ''
  )
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  return (
    <div className="relative bg-white w-full h-full overflow-hidden">
      {/* Main Content */}
      <div className={'absolute w-[1036px] h-[963px] top-[93px] left-[267px]'}>
        <div className={'relative'}>
          <img alt="pick-frame" src={cachedPickFrame} className={'absolute top-0 left-[66px]'} />
        </div>
        <div
          className={
            'border-20 w-full mt-[100px] min-h-full rounded-[60px] bg-prominentBlue border-uriYellow flex flex-wrap justify-center items-center gap-12 px-4 pt-36'
          }
          style={{ backgroundImage: `url(${cachedBgLayer})` }}
        >
          <div className="grid grid-cols-2 gap-y-12 overflow-y-auto max-h-[800px] w-full pt-24 pb-48 px-24 custom-scrollbar">
            {sortedFrames.map((frame) => (
              <CachedFrameThumbnail
                key={frame._id}
                frame={frame}
                isSelected={selectedFrame === frame._id}
                onFrameClick={handleFrameClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div
        className="bg-prominentBlue absolute top-0 right-0 py-[100px] px-[100px] h-full flex flex-col items-center justify-center gap-[30px]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      >
        <div className="relative">
          {selectedFrame && (
            <img
              crossOrigin="anonymous"
              alt="frame-preview"
              src={cachedFrameThumbnail}
              className="w-[320px] object-contain drop-shadow-2xl"
            />
          )}
        </div>
        {frameTimeout > 0 && (
          <div className="text-center text-white text-[50px] font-serif-bahela">
            {formatTime(remainingTime)}
          </div>
        )}
        <Button
          onClick={handleContinue}
          className={`text-[50px] ${!selectedFrame ? 'opacity-30' : 'opacity-100'}`}
          textColor="prominentBlue"
          disabled={!selectedFrame}
        >
          {i18n.language === 'id' ? 'Lanjut' : 'Next'}
        </Button>
      </div>

      {/* Bahela Logo */}
      <img
        src={cachedBahelaLogo}
        alt="Bahela logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
    </div>
  )
}

// Component for cached frame thumbnails
const CachedFrameThumbnail: React.FC<{
  frame: { _id: string; name: string; thumbnail: string }
  isSelected: boolean
  onFrameClick: (frameId: string) => void
}> = ({ frame, isSelected, onFrameClick }) => {
  const cachedSrc = useCachedImage(frame.thumbnail)
  return (
    <div className="relative flex justify-center">
      <img
        crossOrigin="anonymous"
        alt={frame.name}
        src={cachedSrc}
        className={`object-contain cursor-pointer drop-shadow-2xl w-[300px] ${
          isSelected ? 'outline outline-8 outline-uriYellow rounded-[10px]' : ''
        }`}
        onClick={() => onFrameClick(frame._id)}
      />
    </div>
  )
}

export default FramePage
